import { Link } from 'react-router';
import { Avatar } from '@/components/brand/Brand';
import { Button, ButtonLink } from '@/components/ui/Button';
import { EmptyState, Skeleton } from '@/components/ui/Feedback';
import { SectionBar } from '@/components/ui/SectionBar';
import { useFriendRequests, useFriends, usePlayingPartners } from '@/lib/api/queries';
import { MONTHS } from '@/lib/format/date';
import { fmtDecimal } from '@/lib/format/number';
import { routes } from '@/routes/paths';
import { useRespondToRequest } from './useRespondToRequest';
import styles from './Friends.module.css';
import { t } from '@/lib/i18n';
import { ChevronLeftIcon } from '@/components/icons';

export function FriendsList() {
  const friends = useFriends();
  const requests = useFriendRequests();
  const partners = usePlayingPartners();
  const { working, respond } = useRespondToRequest();

  const received = requests.data?.received ?? [];
  const list = friends.data ?? [];
  const loading = friends.isLoading || requests.isLoading;

  if (loading) return <FriendsSkeleton />;

  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <div className={styles.heroNav}>
          <Link to={routes.home} className={styles.back} aria-label={t('friends.back')}>
            <ChevronLeftIcon size={22} />
          </Link>
        </div>
        <h1 className={styles.title}>{t('friends.label')}</h1>
        <p className={styles.subtitle}>{describeCounts(list.length, received.length)}</p>
      </div>

      {received.length > 0 && (
        <>
          <SectionBar variant="mid">
            {t('friends.requests', { count: received.length })}
          </SectionBar>
          {received.map((request) => (
            <div key={request.id} className={styles.request}>
              <div className={styles.requestHead}>
                <Avatar name={request.user.displayName} size={40} variant="green" />
                <div className={styles.rowBody}>
                  <div className={styles.rowName}>{request.user.displayName}</div>
                  <div className={styles.rowMeta}>{describeHandicap(request.user.handicapIndex)}</div>
                </div>
              </div>
              <div className={styles.requestActions}>
                <Button
                  size="sm"
                  busy={working === request.id}
                  onClick={() => void respond(request, true)}
                >
                  {t('friends.accept')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={working === request.id}
                  onClick={() => void respond(request, false)}
                >
                  {t('friends.decline')}
                </Button>
              </div>
            </div>
          ))}
        </>
      )}

      <SectionBar aside={list.length > 1 ? t('friends.sortAZ') : undefined}>{t('friends.title')}</SectionBar>

      {list.length === 0 ? (
        <EmptyState
          title={t('friends.empty')}
          actions={<ButtonLink to={routes.addFriend}>{t('friends.add')}</ButtonLink>}
        >
          {t('friends.emptyBody')}
        </EmptyState>
      ) : (
        <>
          {list.map((friend) => (
            /* A linha inteira leva ao perfil (canvas 3a) — o alvo de toque é a linha, não o nome. */
            <Link key={friend.id} to={routes.player(friend.id)} className={styles.row}>
              <Avatar name={friend.displayName} size={40} variant="green" />
              <div className={styles.rowBody}>
                <div className={styles.rowName}>{friend.displayName}</div>
                {/*
                  "14 voltas juntos" era a linha que a Fase A não podia calcular — sem volta em
                  grupo não havia o que contar. Agora há, e ela substitui "amigos desde": o que
                  situa a dupla é quanto jogaram juntos, não a data do aperto de mão.
                */}
                <div className={styles.rowMeta}>
                  {describeTogether(partners.data?.get(friend.id) ?? 0, friend.friendsSince)}
                </div>
              </div>
              <div className={styles.hcp}>
                <div className={styles.hcpValue}>{fmtDecimal(friend.handicapIndex)}</div>
                <div className={styles.hcpLabel}>{t('friends.hcp')}</div>
              </div>
            </Link>
          ))}
          <div className={styles.footer}>
            <ButtonLink to={routes.addFriend} variant="secondary" block size="lg">
              {t('friends.add')}
            </ButtonLink>
          </div>
        </>
      )}
    </div>
  );
}

function describeCounts(friends: number, requests: number): string {
  const f = t('friends.count', { count: friends });
  if (requests === 0) return f;
  return `${f} · ${t('friends.requestCount', { count: requests })}`;
}

/** "Handicap 16,8", ou o que dá pra dizer de quem ainda não tem índice. */
export function describeHandicap(handicapIndex: number | null): string {
  return handicapIndex === null
    ? t('friends.noHandicap')
    : t('friends.handicap', { value: fmtDecimal(handicapIndex) ?? '' });
}

/**
 * "14 voltas juntos" quando houve alguma; a data da amizade quando ainda não. Zero não vira
 * "0 voltas juntos" porque isso leria como placar, quando o fato é que a dupla é nova.
 */
function describeTogether(rounds: number, friendsSince: string): string {
  if (rounds === 0) return t('friends.since', { date: fmtMonthYear(friendsSince) });
  return t('friends.roundsTogether', { count: rounds });
}

/** "ago 2026" — a tabela de meses vem do `date.ts`, que a lê do catálogo. */
function fmtMonthYear(iso: string): string {
  const d = new Date(iso);
  return `${MONTHS[d.getMonth()] ?? ''} ${d.getFullYear()}`;
}

function FriendsSkeleton() {
  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <div className={styles.heroNav} />
        <Skeleton width="45%" height={26} style={{ opacity: 0.25 }} />
        <Skeleton width="30%" height={12} style={{ marginTop: 8, opacity: 0.25 }} />
      </div>
      {[0, 1, 2].map((i) => (
        <div key={i} className={styles.row}>
          <Skeleton width={40} height={40} radius="999px" />
          <div className={styles.rowBody}>
            <Skeleton width="55%" height={14} />
            <Skeleton width="35%" height={10} style={{ marginTop: 6 }} />
          </div>
        </div>
      ))}
    </div>
  );
}
